import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'
import { PartnerFormPopup } from '@indipia/shared-features'
import { Button, Badge, Combobox, Checkbox, Tabs, EmptyState, Modal, Skeleton, Alert, Segmented, TextLink, StatusHint, IconCheckCircle, type ComboboxOption } from '@indipia/ui-core'
import { scheduleApi, ForemanConfirmSendError } from '../api/scheduleApi'
import {
  getPartnerCandidates, getForemanConfirmResults, getNoticePreviews, changeConfirmedPartner, releasePartner,
  type PartnerChangeReasonCode, type PartnerCandidate, type PartnerCandidatesResult, type ForemanConfirmResults,
  type ForemanConfirmTaskItem, type ForemanNextPartner, type NoticePreviewItem,
} from '../api/foremanApi'
import { PartnerCandidateItem, conflictConfirmText, formatMonthDay, formatMonthDayTime } from '../components/PartnerCandidateItem'
import { ErrorBanner } from '../components/AsyncState'
import { AssignEvidenceModal } from '../components/AssignEvidenceModal'
import { useModalA11y } from '../hooks/useModalA11y'
import { useInvalidateSignals } from '../hooks/useTaskSignals'
import { useImmediateConfirm, isFixedPatchResponse, IMMEDIATE_CONFIRM_SUCCESS_MESSAGE } from '../hooks/useImmediateConfirm'
import { Z_POPUP } from '../constants/zIndex'
import { getSendAction, getPartnerSaveKind, PARTNER_STATUS, type SendAction } from '../constants/partnerStatus'
import { foremanSendErrorMessage, foremanChangeErrorMessage, FOREMAN_CHANGE_MESSAGE } from '../constants/foremanSendMessage'
import { ForemanChangeNotice, foremanChangeResultMessage } from '../components/ForemanChangeNotice'
import {
  ACTION_REASON_LABEL, PLAN_LABEL, PLAN_REASON_TEXT, FAIL_REASON_LABEL,
  HISTORY_STATUS_BADGE, CLOSED_REASON_LABEL, SEND_METHOD_LABEL,
} from '../constants/foremanPlan'

export const FOREMAN_CONFIRM_TITLE = '시공파트너 배정 안내'

// 링크의 실제 유효기간(expiresAt)이 이미 지났으면, 기간만료 버튼을 누르지 않았어도
// 사실상 만료된 것으로 본다.
function isPastExpiry(expiresAt: string | null): boolean {
  if (!expiresAt) return false
  const t = new Date(expiresAt).getTime()
  return !isNaN(t) && t < Date.now()
}
// 후보 콤보박스의 안내용 가상 옵션 값(실제 파트너 id와 겹치지 않는 센티널)
const OPT_NONE = ''
const OPT_EMPTY = '__empty__'
const OPT_ERROR = '__error__'

// 확인 모달 상태 — 일정 겹침 확인·추천대로 모두 보내기·반장님 등록 안내가 한 모달을 공유한다.
interface ConfirmState {
  title: string
  body: React.ReactNode
  confirmLabel: string
  onConfirm: () => void
}

// 회신 결과 탭 조회 상태
type ResultsState = 'loading' | 'ok' | 'error'

// 알림톡 예시 4종 — 문구는 서버(GET notice-previews) 렌더 결과를 그대로 쓴다.
type NoticeTab = 'CONFIRM' | 'CLOSED' | 'CHANGE' | 'CANCEL'
const NOTICE_TAB_OPTIONS: { label: string; value: NoticeTab }[] = [
  { label: '일정확인', value: 'CONFIRM' },
  { label: '마감 안내', value: 'CLOSED' },
  { label: '일정 변경', value: 'CHANGE' },
  { label: '배정 취소', value: 'CANCEL' },
]

export interface SigongTask {
  taskId:         number
  workTypeCode?:  string
  workTypeName:   string
  startDate:      Date
  endDate:        Date
  color:          string
  foremanName:    string | null
  foremanContact: string | null
  // 반장 상태 코드(E01_TASK_PARTNER_STATUS) — 회신 결과 판정은 constants/partnerStatus 헬퍼 사용
  partnerStatus:  string
  // 반장 일정확인 회신 시각(ISO). null=미회신
  respondedDtm:   string | null
  // 일정확인 발송 시각(ISO). null=미발송
  sentDtm:        string | null
  // 배정 출처(서버 assignSource: AUTO=자동배정, MANUAL=직접배정). 전달하지 않으면 배정 출처 배지를 그리지 않는다.
  assignSource?:  string | null
  // 배정 반장 id(없으면 null). 전달하지 않으면(undefined) foremanName 으로 partners 에서 찾는다.
  assigneePartnerId?: number | null
  // SIF-1 '자재파트너가 시공' 공종(현장별 방식 override 반영 최종값) — true 면 배정·일정확인 대상에서 제외하고 안내만 보인다
  constructByVendor?: boolean
}

export interface SigongPartner {
  partnerId:      number
  partnerName:    string
  mobile?:         string
  specialtyTypes?: string | null
}

// 배정/발송이 성공했을 때 부모에게 넘기는 변경분 — 부모는 이 값으로 자기 tasks 상태를
// 국소 갱신(setTasks(prev => prev.map(...)))하면 되고, 화면 전체를 다시 불러올 필요가 없다.
export interface SigongAssignUpdate {
  taskId:         number
  foremanName:    string | null
  foremanContact: string | null
  /** 실제로 배정된 반장이 바뀐 경우만 true — BE가 이전 응답(확인 상태)을 초기화하므로
   *  (TaskService#patchTask의 resetConfirmResponse) 부모 쪽 partnerStatus/sentDtm/respondedDtm도 맞춰 초기화한다. */
  resetConfirm?:  boolean
  /** 일정확인을 방금 새로 보낸 경우 그 발송시각(ISO) — 부모 쪽 partnerStatus(WAITING)/sentDtm 갱신용 */
  sentDtm?:       string
  /** 발송 응답의 반장 상태(WAITING) — sentDtm과 함께 부모 tasks에 반영 */
  partnerStatus?: string
  /** 배정 반장 id가 바뀐 경우(해제=null). 생략하면 부모는 기존 값을 유지한다 */
  assigneePartnerId?: number | null
  /** 배정 출처가 바뀐 경우(해제=null). 생략하면 부모는 기존 값을 유지한다 */
  assignSource?: string | null
  /** 착공 D-2 이내 수동 배정이 일정확인 없이 즉시 확정된 경우 true — 부모는 partnerStatus=CONFIRMED 로 갱신한다 */
  fixed?:         boolean
}

interface Props {
  tasks:             SigongTask[]
  partners:          SigongPartner[]
  siteName?:         string
  scheduleId?:       number
  onClose:           () => void
  onPartnerCreated?: () => void
  onAssigned?:       (update: SigongAssignUpdate) => void
  /** 시공파트너 일정확인 설정 화면으로 이동 — 없으면 회신 결과 탭의 설정 링크를 그리지 않는다 */
  onOpenSettings?:   () => void
}

export function SigongPopup({ tasks: allTasks, partners, siteName, scheduleId, onClose, onPartnerCreated, onAssigned, onOpenSettings }: Props) {
  // 자재시공 공종은 배정·발송 목록·기본 체크·발송 대상에서 뺀다(안내 한 줄만 표시)
  const tasks = useMemo(() => allTasks.filter(t => !t.constructByVendor), [allTasks])
  const vendorTaskNames = useMemo(() => allTasks.filter(t => t.constructByVendor).map(t => t.workTypeName), [allTasks])
  const fmtDate = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`
  const user = useAuthStore(s => s.user)
  // 확인 모달이 떠 있는 동안 Esc는 모달만 닫아야 하므로, 팝업 닫기는 모달 상태를 보고 막는다.
  const confirmOpenRef = useRef(false)
  const { boxRef, titleId } = useModalA11y(true, () => { if (!confirmOpenRef.current) onClose() })
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null)
  // '자동배정 ⓘ' 배지를 눌러 연 자동매칭 근거 팝업 대상 공종
  const [evidenceTask, setEvidenceTask] = useState<SigongTask | null>(null)
  // 착공 D-2 이내 수동 배정 즉시 확정 확인(서버 409 IMMEDIATE_CONFIRM_REQUIRED)
  const immediate = useImmediateConfirm()
  const invalidateSignals = useInvalidateSignals()
  // 배정·발송·변경·해제 성공 결과를 올려 보낼 때 신호도 함께 재조회한다
  const notifyAssigned = (update: SigongAssignUpdate) => { void invalidateSignals(); onAssigned?.(update) }
  confirmOpenRef.current = confirmState != null

  // 화면은 항상 배정 · 발송 탭으로 열린다(기획서 C-1-9).
  const [tab,      setTab]      = useState<'kakao' | 'result'>('kakao')
  const [showPartnerPopup, setShowPartnerPopup] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(tasks.filter(t => t.foremanName).map(t => t.taskId))
  )
  const [periods] = useState<Record<number, string>>(
    () => Object.fromEntries(tasks.map(t => [t.taskId, `${fmtDate(t.startDate)} ~ ${fmtDate(t.endDate)}`]))
  )
  const [foremen,  setForemen]  = useState<Record<number, number | null>>({})
  const [assigningTaskId, setAssigningTaskId] = useState<number | null>(null)
  // 선택 즉시 저장 성공 표시 — 다음 변경이 시작되면 안내 문구로 되돌린다
  const [justSaved, setJustSaved] = useState(false)
  const [sending, setSending] = useState(false)
  // 직접 지정으로 고른 반장 이름(partners 목록에 없는 후보를 골랐을 때 콤보박스 표시용)
  const [foremanNames, setForemanNames] = useState<Record<number, string>>({})
  // 확정(CONFIRMED) 반장을 다른 반장으로 바꾸려고 고른 임시 선택은 foremen 에 보류해 두고(저장 안 함),
  // 행 아래 경고·사유 영역에서 확정한다. 공종별 변경 사유 코드·행 오류 문구·처리 중 공종.
  const [changeReasons, setChangeReasons] = useState<Record<number, string>>({})
  const [changeErrors, setChangeErrors] = useState<Record<number, string>>({})
  const [changingTaskId, setChangingTaskId] = useState<number | null>(null)
  // taskId → 그 공종의 FOREMAN_CONFIRM 발송이력 전체(최초 발송 + 재전송들), 발송시각 오름차순.
  // 미배정으로 되돌릴 때 그 반장 앞으로 살아있는 안내 링크를 회수(revokeActiveLinks)하는 데만 쓴다.
  // recipientName도 함께 보관 — 반장이 변경되면 같은 taskId에 예전 반장 앞으로 보낸 이력도 섞여
  // 남아있으므로, 현재 배정된 반장(task.foremanName) 이름과 일치하는 건만 걸러내야 한다.
  const [historyMap, setHistoryMap] = useState<Map<number, { historyId: number; revokedYn: string; sentDtm: string | null; recipientName: string | null; viewedDtm: string | null; expiresDtm: string | null }[]>>(new Map())
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)

  // ── 후보 조회 캐시(공종 단위, 팝업이 열려 있는 동안 유지) ─────────────────────────────
  // 행을 펼칠 때(또는 회신 결과 탭의 "다른 반장님" 메뉴를 열 때) 해당 공종만 조회하고,
  // 같은 공종은 재조회하지 않는다. 배정·발송이 성공하면 그 공종 캐시를 비운다.
  const candPromiseRef = useRef(new Map<number, Promise<PartnerCandidatesResult>>())
  const candResultRef  = useRef(new Map<number, PartnerCandidatesResult>())
  const [, setCandVersion] = useState(0)
  const fetchCandidates = useCallback((taskId: number): Promise<PartnerCandidatesResult> => {
    const cached = candPromiseRef.current.get(taskId)
    if (cached) return cached
    const p = getPartnerCandidates(taskId).then(r => {
      // 캐시가 그 사이 비워졌으면(배정·발송 성공) 오래된 응답을 저장하지 않는다
      if (candPromiseRef.current.get(taskId) === p) {
        candResultRef.current.set(taskId, r)
        setCandVersion(v => v + 1)
      }
      return r
    })
    p.catch(() => { if (candPromiseRef.current.get(taskId) === p) candPromiseRef.current.delete(taskId) })
    candPromiseRef.current.set(taskId, p)
    return p
  }, [])
  const invalidateCandidates = useCallback((taskId?: number) => {
    if (taskId == null) { candPromiseRef.current.clear(); candResultRef.current.clear() }
    else { candPromiseRef.current.delete(taskId); candResultRef.current.delete(taskId) }
    setCandVersion(v => v + 1)
  }, [])

  // ── 회신 결과(서버 foreman-confirm-results) — 팝업 오픈 시 1회 조회, 발송·배정 변경 후 재조회 ──
  const [results, setResults] = useState<ForemanConfirmResults | null>(null)
  const [resultsState, setResultsState] = useState<ResultsState>('loading')
  const resultsSeqRef = useRef(0)
  const loadResults = useCallback(async (silent = false) => {
    if (scheduleId == null) return
    const seq = ++resultsSeqRef.current
    if (!silent) setResultsState('loading')
    try {
      const r = await getForemanConfirmResults(scheduleId)
      if (seq !== resultsSeqRef.current) return
      setResults(r)
      setResultsState('ok')
    } catch {
      if (seq !== resultsSeqRef.current) return
      // 재조회(silent) 실패는 기존 화면을 유지하고, 첫 조회 실패만 오류 안내를 보인다
      if (!silent) setResultsState('error')
    }
  }, [scheduleId])
  useEffect(() => { void loadResults() }, [loadResults])
  const actionCount = results?.summary.actionCount ?? 0

  // 회신 결과 탭 상태
  const [otherTargets, setOtherTargets] = useState<Record<number, ForemanNextPartner>>({}) // 다른 반장님으로 고른 발송 대상(공종별)
  const [openMenu, setOpenMenu] = useState<{ taskId: number; kind: 'other' | 'more' } | null>(null)
  const [busyTaskIds, setBusyTaskIds] = useState<Set<number>>(new Set())
  const [bulkSending, setBulkSending] = useState(false)
  const [candErrorIds, setCandErrorIds] = useState<Set<number>>(new Set()) // "다른 반장님" 후보 조회 실패 공종
  const menuRootRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!openMenu) return
    const onDown = (e: MouseEvent) => {
      if (menuRootRef.current && !menuRootRef.current.contains(e.target as Node)) setOpenMenu(null)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [openMenu])

  useEffect(() => {
    if (scheduleId == null) return
    const hdrs = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
    apiClient.get<{ data: { historyId: number; taskId: number | null; revokedYn: string; sentDtm: string | null; recipientName: string | null; viewedDtm: string | null; expiresDtm: string | null }[] }>(
      `/schedule/schedules/${scheduleId}/share-history`,
      { params: { dispatchType: 'FOREMAN_CONFIRM' }, headers: hdrs },
    )
      .then(res => {
        const list = res.data?.data ?? []
        const map = new Map<number, { historyId: number; revokedYn: string; sentDtm: string | null; recipientName: string | null; viewedDtm: string | null; expiresDtm: string | null }[]>()
        list.forEach(h => {
          if (h.taskId == null) return
          const entries = map.get(h.taskId) ?? []
          entries.push({ historyId: h.historyId, revokedYn: h.revokedYn, sentDtm: h.sentDtm, recipientName: h.recipientName, viewedDtm: h.viewedDtm, expiresDtm: h.expiresDtm })
          map.set(h.taskId, entries)
        })
        // 서버가 발송시각 내림차순으로 내려주므로, 화면에는 오래된 순(최초 발송이 위, 재전송이 아래)으로 뒤집는다.
        map.forEach(entries => entries.reverse())
        setHistoryMap(map)
      })
      .catch(() => {})
  }, [scheduleId, user?.compId, historyRefreshKey])

  // 반환: true=저장됨 · false=착공 D-2 이내 즉시 확정 확인에서 취소(저장 안 함 — 호출부가 선택을 되돌린다)
  const assignForeman = async (taskId: number, partnerId: number | null, picked?: { name: string; mobile: string | null }): Promise<boolean> => {
    setAssigningTaskId(taskId)
    try {
      const hdrs = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
      const r = await immediate.run(ok => apiClient.patch(`/schedule/tasks/${taskId}`, {
        assigneePartnerId: partnerId,
        updatePredecessor: false,
        ...(ok && { confirmImmediate: true }),
      }, { headers: hdrs }))
      if (r.cancelled) return false
      const fixed = isFixedPatchResponse(r.value)
      // BE(TaskService#patchTask)는 배정된 반장이 실제로 바뀐 경우에만 이전 응답을 초기화한다 —
      // 같은 반장으로 재배정(예: 이미 배정된 상태에서 발송만 다시 하는 경우)이면 확인 상태를 유지해야 한다.
      const task = tasks.find(t => t.taskId === taskId)
      const oldMatchedId = task ? currentPartnerIdOf(task) : null
      const partner = partnerId != null ? partners.find(p => p.partnerId === partnerId) : undefined
      notifyAssigned({
        taskId,
        foremanName:    partner?.partnerName ?? picked?.name ?? null,
        foremanContact: partner?.mobile ?? picked?.mobile ?? null,
        resetConfirm:   partnerId !== oldMatchedId && !fixed,
        assigneePartnerId: partnerId,
        ...(partnerId !== oldMatchedId && { assignSource: partnerId == null ? null : 'MANUAL' }),
        ...(fixed && { fixed: true }),
      })
      if (fixed) toast.success(IMMEDIATE_CONFIRM_SUCCESS_MESSAGE)
      // 배정이 바뀌면 그 공종의 후보 표시(현재 배정·이미 보냄)가 달라지므로 캐시를 비우고 회신 결과를 다시 받는다.
      invalidateCandidates(taskId)
      void loadResults(true)
      setJustSaved(true)
      return true
    } catch {
      toast.error('시공파트너 배정 중 오류가 발생했습니다.')
      throw new Error('assign failed')
    } finally {
      setAssigningTaskId(null)
    }
  }

  const assignable   = tasks.filter(t => !!t.foremanName || foremen[t.taskId] !== undefined)

  // ── 발송 판정(순수 판정은 constants/partnerStatus#getSendAction) ─────────────
  // 현재 반장 = task.foremanName으로 partners에서 찾은 id(삭제된 반장이면 null).
  // 부모가 assigneePartnerId 를 주면 그 값, 아니면 이름으로 찾는다.
  const currentPartnerIdOf = (t: SigongTask): number | null =>
    t.assigneePartnerId !== undefined ? t.assigneePartnerId
      : t.foremanName ? (partners.find(p => p.partnerName === t.foremanName)?.partnerId ?? null) : null
  // 확정 반장을 다른 반장으로 바꾸려고 보류 중인 공종인지 — 확정 상태 + 현재 반장 있음 + 다른 반장을 골라 둠
  const pendingChangeIdOf = (t: SigongTask): number | null => {
    const sel = foremen[t.taskId]
    const cur = currentPartnerIdOf(t)
    return t.partnerStatus === PARTNER_STATUS.CONFIRMED && cur != null && typeof sel === 'number' && sel !== cur ? sel : null
  }
  // 이 공종에 지금 화면에서 고른 반장 id — 콤보박스로 고르지 않았으면 현재 반장.
  const selectedPartnerIdOf = (t: SigongTask): number | null => {
    const explicit = foremen[t.taskId]
    return explicit !== undefined ? explicit : currentPartnerIdOf(t)
  }
  const sendActionOf = (t: SigongTask): SendAction => {
    const explicit = foremen[t.taskId]
    const selId = selectedPartnerIdOf(t)
    const hasPartner = selId != null || (explicit === undefined && !!t.foremanName)
    const isCurrent = explicit === undefined || (selId != null && selId === currentPartnerIdOf(t))
    return getSendAction(t, { hasPartner, isCurrent })
  }

  // 한 공종(taskId)에 대해 일정확인 카톡 1건을 서버 API로 발송 — 링크 발급·알림톡·이력 기록·상태 전이는 서버가 수행한다.
  // 성공 시 응답(partnerStatus·sentDtm)을 부모 tasks에 반영한다. 실패하면 ForemanConfirmSendError를 던진다.
  const sendForemanConfirmOne = async (t: SigongTask, action: SendAction, partnerId: number | null) => {
    if (action.method == null) throw new Error('send not allowed')
    // 다시 보내기(RESEND)는 partnerId(현재 반장) 필수 — 반장 번호를 모르면 호출하지 않고 기존 안내(notAllowed)로 거절한다.
    const resendPartnerId = action.method === 'RESEND' ? currentPartnerIdOf(t) : null
    if (action.method === 'RESEND' && resendPartnerId == null) {
      throw new ForemanConfirmSendError('SEND_NOT_ALLOWED', null, null, null)
    }
    const res = await scheduleApi.sendForemanConfirm(t.taskId, {
      sendMethod: action.method,
      ...(action.method === 'MANUAL' && action.withPartnerId && partnerId != null && { partnerId }),
      ...(resendPartnerId != null && { partnerId: resendPartnerId }),
    })
    const sentPartner = partners.find(p => p.partnerId === res.partnerId)
    notifyAssigned({
      taskId:         t.taskId,
      foremanName:    sentPartner?.partnerName ?? t.foremanName,
      foremanContact: sentPartner?.mobile ?? t.foremanContact,
      sentDtm:        res.sentDtm,
      partnerStatus:  res.partnerStatus,
      assigneePartnerId: res.partnerId,
      ...(action.method === 'MANUAL' && action.withPartnerId && { assignSource: 'MANUAL' }),
    })
    return res
  }

  // 발송 성공·실패 뒤(실패도 서버 이력에 기록됨) 공종 후보 캐시·회신 결과·발송이력을 다시 받는다.
  const refreshAfterSend = (taskIds: number[]) => {
    taskIds.forEach(id => invalidateCandidates(id))
    setHistoryRefreshKey(k => k + 1)
    void loadResults(true)
  }

  // 회신 결과 탭 한 줄에서 발송 — 방식 MANUAL(partnerId=지정 발송) / RESEND(현재 반장 다시 보내기).
  // 성공하면 부모 tasks(partnerStatus·sentDtm)를 갱신하고, 회신 결과·후보 캐시를 다시 받는다.
  const sendFromResult = async (
    item: ForemanConfirmTaskItem,
    req: { method: 'MANUAL'; partner: ForemanNextPartner; mobile?: string | null } | { method: 'RESEND' },
  ): Promise<boolean> => {
    if (busyTaskIds.has(item.taskId)) return false
    setBusyTaskIds(prev => new Set(prev).add(item.taskId))
    try {
      if (req.method === 'RESEND' && item.partnerId == null) {
        throw new ForemanConfirmSendError('SEND_NOT_ALLOWED', null, null, null)
      }
      const res = await scheduleApi.sendForemanConfirm(
        item.taskId,
        req.method === 'MANUAL' ? { sendMethod: 'MANUAL', partnerId: req.partner.partnerId }
          : { sendMethod: 'RESEND', partnerId: item.partnerId! },
      )
      const sentPartner = partners.find(p => p.partnerId === res.partnerId)
      const parentTask = tasks.find(t => t.taskId === item.taskId)
      notifyAssigned({
        taskId:         item.taskId,
        foremanName:    sentPartner?.partnerName ?? (req.method === 'MANUAL' ? req.partner.partnerName : item.partnerName ?? parentTask?.foremanName ?? null),
        foremanContact: sentPartner?.mobile ?? (req.method === 'MANUAL' ? req.mobile ?? null : item.partnerPhone ?? parentTask?.foremanContact ?? null),
        sentDtm:        res.sentDtm,
        partnerStatus:  res.partnerStatus,
        assigneePartnerId: res.partnerId,
        ...(req.method === 'MANUAL' && { assignSource: 'MANUAL' }),
      })
      setForemen(prev => { const next = { ...prev }; delete next[item.taskId]; return next })
      setOtherTargets(prev => { const next = { ...prev }; delete next[item.taskId]; return next })
      return true
    } catch (err) {
      toast.error(`${item.workTypeName} · ${foremanSendErrorMessage(err)}`)
      return false
    } finally {
      setBusyTaskIds(prev => { const next = new Set(prev); next.delete(item.taskId); return next })
      refreshAfterSend([item.taskId])
    }
  }

  // 반장 배정을 "미배정"으로 되돌릴 때 — 그 반장 앞으로 아직 살아있는(미회수·미만료)
  // 시공파트너 배정 안내 링크가 있으면 전부 회수(만료) 처리한다. 배정이 취소된 반장에게
  // 예전 링크가 계속 유효하게 남아있으면 안 되기 때문(반장이 바뀌어도 응답 가능해짐 방지).
  const revokeActiveLinks = async (taskId: number, foremanName: string | null) => {
    if (scheduleId == null || !foremanName) return 0
    const targets = (historyMap.get(taskId) ?? []).filter(e =>
      e.recipientName === foremanName && e.historyId != null && e.revokedYn !== 'Y' && !isPastExpiry(e.expiresDtm)
    )
    if (targets.length === 0) return 0
    const hdrs = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
    await Promise.all(targets.map(t =>
      apiClient.post(`/schedule/schedules/${scheduleId}/share-history/${t.historyId}/revoke`, {}, { headers: hdrs }).catch(() => {})
    ))
    setHistoryMap(prev => {
      const next = new Map(prev)
      const entries = next.get(taskId)
      if (entries) {
        const revokedIds = new Set(targets.map(t => t.historyId))
        next.set(taskId, entries.map(e => revokedIds.has(e.historyId) ? { ...e, revokedYn: 'Y' } : e))
      }
      return next
    })
    return targets.length
  }

  // 선택한 공종마다 서버 발송 API를 순차 호출한다(공종 단위 API). 한 건 실패가 다른 건을 막지 않는다.
  // 반장 지정(배정)은 콤보박스 선택 시점에 이미 반영되며, 다른 반장 지정 발송은 서버가 성공 시 그 반장으로 지정한다.
  const sendTargets = assignable.filter(t => selected.has(t.taskId) && sendActionOf(t).enabled)
  // 발송 버튼 N — 발송 대상 공종의 반장 중복 제외 인원. 반장 id를 모르는(이름만 남은) 공종은 이름으로 구분한다.
  const partnerKeyOf = (t: SigongTask): string => {
    const id = selectedPartnerIdOf(t)
    return id != null ? `id:${id}` : `name:${foremanNames[t.taskId] ?? t.foremanName ?? ''}`
  }
  const sendPartnerCount = new Set(sendTargets.map(partnerKeyOf)).size
  const handleSend = async () => {
    const targets = sendTargets
    if (targets.length === 0 || sending) return
    setSending(true)
    try {
      const sentIds = new Set<number>()
      const failures = new Map<string, string[]>() // 안내 문구 → 공종명들
      for (const t of targets) {
        try {
          await sendForemanConfirmOne(t, sendActionOf(t), selectedPartnerIdOf(t))
          sentIds.add(t.taskId)
        } catch (err) {
          const msg = foremanSendErrorMessage(err)
          failures.set(msg, [...(failures.get(msg) ?? []), t.workTypeName])
        }
      }
      // 성공한 공종의 임시 선택(foremen)만 비운다 — 실패한 공종은 고른 반장 그대로 두어 다시 시도할 수 있게 한다.
      setForemen(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !sentIds.has(Number(k)))))
      if (sentIds.size > 0) toast.success(`일정확인 카톡을 보냈어요 · ${sentIds.size}건`)
      failures.forEach((names, msg) => toast.error(`${msg} · ${names.join(', ')}`))
      // 실패 건도 서버 이력에 기록되므로 후보 캐시·회신 결과·발송이력을 다시 조회한다.
      refreshAfterSend(targets.map(t => t.taskId))
      if (sentIds.size > 0) setTab('result')
    } finally {
      setSending(false)
    }
  }
  const allSelected  = assignable.length > 0 && assignable.every(t => selected.has(t.taskId))
  const someIndet    = !allSelected && assignable.some(t => selected.has(t.taskId))
  const toggleAll    = () => setSelected(allSelected ? new Set() : new Set(assignable.map(t => t.taskId)))
  const toggleOne    = (id: number) => setSelected(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s
  })
  // 선택 수 표기 — 체크된 공종의 반장 중복 제외 인원(명)
  const selPartnerCount = new Set(assignable.filter(t => selected.has(t.taskId)).map(partnerKeyOf)).size
  const [previewIdx, setPreviewIdx] = useState(0)
  const previewList   = assignable.filter(t => selected.has(t.taskId))
  const safeIdx       = previewList.length === 0 ? 0 : Math.min(previewIdx, previewList.length - 1)
  const previewTask   = previewList[safeIdx] ?? assignable[0]
  // 알림톡 예시 — 선택한 공종 기준으로 서버 렌더 문구 5종을 받아 4탭으로 보여준다(공종별 캐시).
  const [noticeTab, setNoticeTab] = useState<NoticeTab>('CONFIRM')
  const [noticeCache, setNoticeCache] = useState<Record<number, NoticePreviewItem[]>>({})
  const [noticeFailed, setNoticeFailed] = useState<Set<number>>(new Set())
  const noticeRequestedRef = useRef(new Set<number>())
  const noticeTaskId = previewTask?.taskId
  useEffect(() => {
    if (noticeTaskId == null || noticeRequestedRef.current.has(noticeTaskId)) return
    noticeRequestedRef.current.add(noticeTaskId)
    getNoticePreviews(noticeTaskId)
      .then(items => setNoticeCache(prev => ({ ...prev, [noticeTaskId]: items })))
      .catch(() => setNoticeFailed(prev => new Set(prev).add(noticeTaskId)))
  }, [noticeTaskId])

  // ── 배정 · 발송 탭: 후보 콤보박스 ────────────────────────────────────────────
  // 후보는 서버가 정렬해 내려준 순서 그대로(재정렬 금지). 첫 항목은 "지정 안 함".
  const loadCandidateOptions = async (task: SigongTask): Promise<ComboboxOption[]> => {
    const none: ComboboxOption = { value: OPT_NONE, label: '지정 안 함' }
    try {
      const r = await fetchCandidates(task.taskId)
      if (r.candidates.length === 0) return [none, { value: OPT_EMPTY, label: '배정 가능한 시공파트너가 없어요' }]
      return [none, ...r.candidates.map(c => ({ value: String(c.partnerId), label: c.partnerName }))]
    } catch {
      return [none, { value: OPT_ERROR, label: '후보를 불러오지 못했어요 · 다시 열어 주세요' }]
    }
  }
  const renderCandidateOption = (task: SigongTask, opt: ComboboxOption): React.ReactNode => {
    if (opt.value === OPT_NONE) return <span style={{ color: 'var(--shell-on-surface-2)' }}>지정 안 함</span>
    if (opt.value === OPT_EMPTY) {
      return (
        <div style={{ lineHeight: 1.5 }}>
          <div style={{ fontWeight: 600 }}>배정 가능한 시공파트너가 없어요</div>
          <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>
            나의 시공파트너에 &lsquo;{task.workTypeName}&rsquo; 작업 파트너를 먼저 등록해 주세요
          </div>
        </div>
      )
    }
    if (opt.value === OPT_ERROR) return <span style={{ color: 'var(--status-danger-600)' }}>{opt.label}</span>
    const cand = candResultRef.current.get(task.taskId)?.candidates.find(c => String(c.partnerId) === opt.value)
    return cand ? <PartnerCandidateItem candidate={cand} /> : opt.label
  }

  // 보류 중이던 확정 반장 변경 선택을 버리고 현재 반장으로 되돌린다(저장·호출 없음).
  const clearPendingChange = (taskId: number) => {
    const drop = <T,>(prev: Record<number, T>): Record<number, T> => { const n = { ...prev }; delete n[taskId]; return n }
    setForemen(drop); setForemanNames(drop); setChangeReasons(drop); setChangeErrors(drop)
  }

  // 확정 반장 변경 확정 — 행 아래 [일정확인 카톡 보내기](sendNow=true) · [변경 저장](sendNow=false).
  // 사유 필수. 성공하면 응답으로 부모 공종 한 건을 갱신하고 보류 선택을 비운다. 실패하면 보류 상태를 유지해 다시 시도할 수 있다.
  const handleConfirmChange = async (task: SigongTask, sendNow: boolean) => {
    const newId = pendingChangeIdOf(task)
    if (newId == null || changingTaskId != null) return
    const reason = changeReasons[task.taskId]
    if (!reason) {
      setChangeErrors(prev => ({ ...prev, [task.taskId]: FOREMAN_CHANGE_MESSAGE.reasonInvalid }))
      return
    }
    setChangingTaskId(task.taskId)
    setChangeErrors(prev => { const n = { ...prev }; delete n[task.taskId]; return n })
    try {
      const run = await immediate.run(ok => changeConfirmedPartner(task.taskId, {
        newPartnerId: newId, reasonCode: reason as PartnerChangeReasonCode, sendNow,
        ...(ok && { confirmImmediate: true }),
      }))
      if (run.cancelled) return   // 취소 — 보류 선택(반장·사유)은 그대로 두어 다시 고를 수 있게 한다
      const r = run.value
      const partner = partners.find(p => p.partnerId === newId)
      const cand = candResultRef.current.get(task.taskId)?.candidates.find(c => c.partnerId === newId)
      const name = partner?.partnerName ?? cand?.partnerName ?? foremanNames[task.taskId] ?? ''
      notifyAssigned({
        taskId:         task.taskId,
        foremanName:    name || null,
        foremanContact: partner?.mobile ?? cand?.mobile ?? null,
        // sendNow=false 면 서버가 ASSIGNED 로 두므로 확인 상태 초기화, true 면 WAITING+발송시각
        resetConfirm:   !r.fixed && r.sent == null,
        ...(r.fixed && { fixed: true }),
        ...(r.sent && { sentDtm: r.sent.sentDtm }),
        partnerStatus:  r.partnerStatus,
        assigneePartnerId: r.partnerId,
        assignSource:   'MANUAL',
      })
      toast.success(r.fixed ? IMMEDIATE_CONFIRM_SUCCESS_MESSAGE : foremanChangeResultMessage(r, name, task.foremanName))
      clearPendingChange(task.taskId)
      setSelected(prev => { const s = new Set(prev); if (sendNow || r.fixed) s.delete(task.taskId); else s.add(task.taskId); return s })
      refreshAfterSend([task.taskId])
      if (sendNow && !r.fixed) setTab('result')
    } catch (err) {
      setChangeErrors(prev => ({ ...prev, [task.taskId]: foremanChangeErrorMessage(err) }))
    } finally {
      setChangingTaskId(null)
    }
  }

  // 행에서 후보를 골랐을 때 — 선택 즉시 저장(PATCH /tasks/{id} assigneePartnerId) 동작은 그대로.
  // 일정 겹침 후보는 확인 후 배정하고, 취소하면 이전 값 그대로 둔다(콤보박스는 value 제어형).
  const handlePickCandidate = (task: SigongTask, val: string) => {
    if (val === OPT_EMPTY || val === OPT_ERROR) return
    setJustSaved(false)
    if (val === OPT_NONE) {
      // 미배정 선택 — 기존에 배정돼 있던 반장이 있었다면 배정을 취소하고,
      // 그 반장 앞으로 발송된(아직 살아있는) 안내 링크도 함께 만료 처리한다.
      // 해제 규칙은 간트 카드와 같다(getPartnerSaveKind): ASSIGNED·WAITING·DECLINED·UNFIXED 만 DELETE partners/{id},
      // 확정 반장은 안내만(보류 중이던 변경 선택은 현재 반장으로 되돌림), 미지정·배정불가는 호출 없음.
      const curId = currentPartnerIdOf(task)
      const kind = getPartnerSaveKind({ partnerStatus: task.partnerStatus, assigneePartnerId: curId }, null)
      if (kind === 'release-blocked') {
        clearPendingChange(task.taskId)
        toast.error(FOREMAN_CHANGE_MESSAGE.releaseDenied)
        return
      }
      if (kind === 'none') {
        if (task.foremanName) { toast.error(FOREMAN_CHANGE_MESSAGE.unknown); return }   // 이름만 남은 삭제된 반장 — id 가 없어 해제할 수 없음
        clearPendingChange(task.taskId)
        return
      }
      const prevForemanName = task.foremanName
      setAssigningTaskId(task.taskId)
      void releasePartner(task.taskId, curId!).then(async () => {
        notifyAssigned({
          taskId: task.taskId, foremanName: null, foremanContact: null, resetConfirm: true,
          assigneePartnerId: null, assignSource: null,
        })
        setForemen(prev => ({ ...prev, [task.taskId]: null }))
        setSelected(prev => { const s = new Set(prev); s.delete(task.taskId); return s })
        invalidateCandidates(task.taskId)
        void loadResults(true)
        setJustSaved(true)
        if (!prevForemanName) return
        const revokedCount = await revokeActiveLinks(task.taskId, prevForemanName)
        if (revokedCount > 0) toast.success('반장 배정을 취소하고, 발송된 안내 링크를 만료 처리했습니다.')
      }).catch(err => {
        toast.error(foremanChangeErrorMessage(err))
      }).finally(() => setAssigningTaskId(null))
      return
    }
    const pid = Number(val)
    const cand = candResultRef.current.get(task.taskId)?.candidates.find(c => c.partnerId === pid)
    // 확정 반장 되돌리기 — 보류 중이던 변경 선택을 취소할 뿐 저장하지 않는다
    if (task.partnerStatus === PARTNER_STATUS.CONFIRMED && pid === currentPartnerIdOf(task)) {
      clearPendingChange(task.taskId)
      return
    }
    const apply = () => {
      // 착공 D-2 이내 즉시 확정 확인에서 취소하면 호출 전 선택으로 되돌린다
      const prevForeman = foremen[task.taskId]
      const prevName = foremanNames[task.taskId]
      const wasSelected = selected.has(task.taskId)
      setForemen(prev => ({ ...prev, [task.taskId]: pid }))
      if (cand) setForemanNames(prev => ({ ...prev, [task.taskId]: cand.partnerName }))
      // 확정 반장을 다른 반장으로 바꾸는 선택은 즉시 저장하지 않고 보류 — 행 아래에서 사유를 고르고 확정한다
      if (task.partnerStatus === PARTNER_STATUS.CONFIRMED && currentPartnerIdOf(task) != null) {
        setSelected(prev => { const s = new Set(prev); s.delete(task.taskId); return s })
        setChangeErrors(prev => { const n = { ...prev }; delete n[task.taskId]; return n })
        return
      }
      setSelected(prev => { const s = new Set(prev); s.add(task.taskId); return s })
      void assignForeman(task.taskId, pid, cand ? { name: cand.partnerName, mobile: cand.mobile } : undefined).then(saved => {
        if (saved) return
        setForemen(prev => {
          const n = { ...prev }
          if (prevForeman === undefined) delete n[task.taskId]; else n[task.taskId] = prevForeman
          return n
        })
        setForemanNames(prev => {
          const n = { ...prev }
          if (prevName === undefined) delete n[task.taskId]; else n[task.taskId] = prevName
          return n
        })
        setSelected(prev => { const s = new Set(prev); if (wasSelected) s.add(task.taskId); else s.delete(task.taskId); return s })
      }).catch(() => {})
    }
    if (cand?.scheduleConflict) {
      setConfirmState({
        title: '일정이 겹쳐요',
        body: <span style={{ whiteSpace: 'pre-line' }}>{conflictConfirmText(cand, '배정')}</span>,
        confirmLabel: '배정',
        onConfirm: apply,
      })
      return
    }
    apply()
  }

  // 배정 상태 배지 — 직접 지정한 건·서버 assignSource=MANUAL 은 직접배정, AUTO 는 자동배정 ⓘ, 반장 없으면 미배정.
  // 자동배정 ⓘ 는 누르면 자동매칭 근거 팝업(AssignEvidenceModal)을 연다.
  const assignBadge = (task: SigongTask, hasPartner: boolean): React.ReactNode => {
    if (!hasPartner) return <Badge status="mute">미배정</Badge>
    const pickedHere = foremen[task.taskId] != null
    if (pickedHere || task.assignSource === 'MANUAL') return <Badge status="info">직접배정</Badge>
    if (task.assignSource === 'AUTO') {
      return (
        <button
          type="button"
          title="자동매칭 근거 보기"
          aria-label="자동매칭 근거 보기"
          onClick={() => setEvidenceTask(task)}
          style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex' }}
        >
          <Badge status="success">자동배정 ⓘ</Badge>
        </button>
      )
    }
    return null
  }

  // ── 회신 결과 탭 ───────────────────────────────────────────────────────────
  const recommendedItems = (results?.actionRequired ?? []).filter(i => i.recommendedPartner != null)
  const handleSendRecommended = async () => {
    if (bulkSending || recommendedItems.length === 0) return
    setBulkSending(true)
    try {
      let ok = 0
      for (const item of recommendedItems) {
        if (await sendFromResult(item, { method: 'MANUAL', partner: item.recommendedPartner! })) ok++
      }
      if (ok > 0) toast.success(`일정확인 카톡을 보냈어요 · ${ok}건`)
    } finally {
      setBulkSending(false)
    }
  }
  const askSendRecommended = () => {
    if (recommendedItems.length === 0) return
    setConfirmState({
      title: `추천대로 ${recommendedItems.length}건을 보낼까요?`,
      body: (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
          {recommendedItems.map(i => <li key={i.taskId}>{i.workTypeName} → {i.recommendedPartner!.partnerName} 님</li>)}
        </ul>
      ),
      confirmLabel: '보내기',
      onConfirm: () => { void handleSendRecommended() },
    })
  }

  // 한 줄 주버튼에서 보낼 대상 — "다른 반장님"으로 고른 대상이 우선, 없으면 🔴 추천 반장 / 🟡 자동 발송 예정 반장
  const mainTargetOf = (item: ForemanConfirmTaskItem, urgent: boolean): ForemanNextPartner | null =>
    otherTargets[item.taskId]
      ?? (urgent ? item.recommendedPartner : item.plan === 'AUTO_SEND' ? item.autoNextPartner : null)
      ?? null
  const handleMainSend = async (item: ForemanConfirmTaskItem, urgent: boolean) => {
    const target = mainTargetOf(item, urgent)
    if (!target) return
    const cand = candResultRef.current.get(item.taskId)?.candidates.find(c => c.partnerId === target.partnerId)
    if (await sendFromResult(item, { method: 'MANUAL', partner: target, mobile: cand?.mobile ?? null })) {
      toast.success('일정확인 카톡을 보냈어요')
    }
  }
  const handleResendRow = async (item: ForemanConfirmTaskItem) => {
    setOpenMenu(null)
    if (await sendFromResult(item, { method: 'RESEND' })) toast.success('일정확인 카톡을 다시 보냈어요')
  }
  // "다른 반장님 ▾" 메뉴 선택 — 대상만 지정(발송은 주버튼). 일정 겹침 후보는 확인 후 지정.
  const handlePickOther = (item: ForemanConfirmTaskItem, cand: PartnerCandidate) => {
    if (cand.alreadySent) return
    const apply = () => {
      setOtherTargets(prev => ({ ...prev, [item.taskId]: { partnerId: cand.partnerId, partnerName: cand.partnerName } }))
      setOpenMenu(null)
    }
    if (cand.scheduleConflict) {
      setConfirmState({
        title: '일정이 겹쳐요',
        body: <span style={{ whiteSpace: 'pre-line' }}>{conflictConfirmText(cand, '발송')}</span>,
        confirmLabel: '선택',
        onConfirm: apply,
      })
      setOpenMenu(null)
      return
    }
    apply()
  }
  const askRegisterPartner = () => setConfirmState({
    title: '시공파트너 등록',
    body: <span>나의 시공파트너에서 반장님을 등록하면 여기서 바로 보낼 수 있어요</span>,
    confirmLabel: '시공파트너 등록',
    onConfirm: () => setShowPartnerPopup(true),
  })
  // "다른 반장님 ▾" — 메뉴를 열 때 그 공종 후보를 지연 조회(캐시 사용). retry=true 는 오류 후 재시도.
  const openOtherMenu = (taskId: number, retry = false) => {
    if (!retry && openMenu?.taskId === taskId && openMenu.kind === 'other') { setOpenMenu(null); return }
    setOpenMenu({ taskId, kind: 'other' })
    setCandErrorIds(prev => { const next = new Set(prev); next.delete(taskId); return next })
    fetchCandidates(taskId).catch(() => {
      setCandErrorIds(prev => new Set(prev).add(taskId))
    })
  }

  const thS: React.CSSProperties = { padding: '8px 10px', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface)', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid var(--shell-border)', background: 'var(--shell-bg)', position: 'sticky', top: 0, zIndex: 1 }
  const tdS: React.CSSProperties = { padding: '9px 10px', fontSize: 12, color: 'var(--shell-on-surface)', verticalAlign: 'middle' }
  // 팝업 폭 1200 — 공종명·알림톡 예시 탭(일정확인·마감 안내·일정 변경·배정 취소)이 줄바꿈·잘림 없게 임시로 넓혔다(예시 칸 360).

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'var(--alpha-black-500)', zIndex: Z_POPUP, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={boxRef}
        style={{ background: 'var(--shell-bg)', borderRadius: 24, width: 1200, maxWidth: '96vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'var(--elevation-3)' }}
      >

        {/* 헤더 */}
        <div style={{ background: 'var(--shell-surface)', padding: '14px 20px', borderBottom: '1px solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span id={titleId} style={{ fontSize: 15, fontWeight: 700, color: 'var(--shell-on-surface)' }}>시공파트너배정-&gt;일정확인알림</span>
            <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', marginTop: 2 }}>배정된 공종을 체크해 일정확인 알림톡을 발송하세요.</div>
          </div>
          {siteName && <span style={{ fontSize: 12, color: 'var(--shell-on-surface-2)' }}>{siteName}</span>}
          <StatusHint
            tone={justSaved ? 'success' : 'muted'}
            icon={justSaved ? <IconCheckCircle width={14} height={14} /> : undefined}
            role="status"
            aria-live="polite"
          >
            {justSaved ? '저장됨' : '변경하면 바로 저장돼요'}
          </StatusHint>
          <button onClick={onClose} aria-label="닫기" style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'var(--shell-border)', color: 'var(--shell-on-surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontFamily: 'inherit', flexShrink: 0, lineHeight: 1 }}>×</button>
        </div>

        {/* 탭 */}
        <Tabs
          variant="line"
          tabs={[
            { label: '배정 · 발송', value: 'kakao' },
            // 지금 처리할 일 건수 — 0이면 배지 없음
            { label: actionCount > 0 ? `회신 결과 🔴${actionCount}` : '회신 결과', value: 'result' },
          ]}
          value={tab}
          onChange={v => {
            // 회신 결과는 팝업 오픈 시 조회하고 발송·배정 변경 때마다 다시 받으므로 탭 전환에서는 재조회하지 않는다.
            // 부모 tasks는 assignForeman/sendFromResult에서 이미 각각 국소 갱신했다.
            setTab(v as 'kakao' | 'result')
            setOpenMenu(null)
          }}
        />

        {/* 탭 내용 */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* ── 시공파트너 배정 탭 ── */}
          {tab === 'kakao' && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>

              {/* 좌측: 공종 목록 */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                {vendorTaskNames.length > 0 && (
                  <div style={{ padding: '10px 16px 0', flexShrink: 0 }}>
                    <Alert kind="info" role="status">
                      {vendorTaskNames.join(' · ')}은 자재파트너가 시공하는 공종이라 배정 · 일정확인 대상이 아니에요
                    </Alert>
                  </div>
                )}
                {/* 전체 선택 바 */}
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--shell-bg)', flexShrink: 0 }}>
                  <Checkbox checked={allSelected} indeterminate={someIndet} onChange={toggleAll} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)' }}>전체 선택</span>
                  <span style={{ marginLeft: 'auto', fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>{selPartnerCount}명 선택됨</span>
                  <Button
                    variant="etc"
                    size="sm"
                    className="!h-auto gap-1 !px-2.5 !py-[5px] text-[length:var(--type-caption-size)]"
                    onClick={() => setShowPartnerPopup(true)}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    신규 시공파트너 등록
                  </Button>
                </div>

                {/* 테이블 */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...thS, width: 32, textAlign: 'center' }}></th>
                        <th style={{ ...thS, width: 20 }}></th>
                        <th style={thS}>공종</th>
                        <th style={thS}>작업기간</th>
                        <th style={thS}>시공파트너</th>
                        <th style={thS}>배정상태</th>
                        {/* 연락처는 별도 칸 없이 시공파트너 선택 아래에 작게 — 좁은 창에서 공종·발송 문구가 여러 줄로 꺾이지 않게 칸을 줄였다 */}
                        <th style={{ ...thS, width: 168 }}>발송</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.length === 0 ? (
                        <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', fontSize: 12, color: 'var(--shell-hint)' }}>공종이 없습니다</td></tr>
                      ) : tasks.map(task => {
                        const matchedId     = currentPartnerIdOf(task) ?? undefined
                        const explicitId    = foremen[task.taskId]
                        const tmpPartnerId  = explicitId !== undefined ? explicitId : matchedId
                        const tmpPartner    = typeof tmpPartnerId === 'number' ? partners.find(p => p.partnerId === tmpPartnerId) : undefined
                        // 이미 배정된 반장이 사후 삭제돼 partners 목록에 없어도(매칭 실패) 이름 자체는
                        // task.foremanName에 남아 있으므로 체크는 가능하게 둔다 — 발송 시 실제 이름·
                        // 연락처는 sendForemanConfirmOne이 task.foremanName/foremanContact로 폴백한다.
                        const canCheck      = typeof tmpPartnerId === 'number' || !!task.foremanName
                        const checked       = selected.has(task.taskId)
                        const displayPhone  = tmpPartner?.mobile ?? task.foremanContact
                        // <select>는 value가 옵션 목록에 없으면 빈칸(placeholder)으로 보인다 — 삭제된
                        // 반장이라 매칭이 안 될 때 배정 자체가 없는 것처럼 보이지 않도록, 실제 배정된
                        // 이름을 담은 비활성 옵션을 하나 끼워 넣어 선택된 상태로 보여준다.
                        const unmatchedAssigned = typeof tmpPartnerId !== 'number' && !!task.foremanName
                        // "미배정"을 목록의 실제 옵션(value='')으로 노출 — 삭제된 반장(displayValue로
                        // 이름을 대신 보여주는 케이스)과 값이 겹치면 안 되므로 그 경우만 별도 센티널을 쓴다.
                        const comboValue = unmatchedAssigned ? '__unmatched__' : String(tmpPartnerId ?? '')
                        // 확정 반장 변경 보류 중이면 일괄 발송에서 제외 — 행 아래 자체 버튼으로만 보낸다
                        const pendingId     = pendingChangeIdOf(task)
                        const rowAction     = pendingId != null
                          ? { label: '아래에서 변경을 확정해 주세요', enabled: false }
                          : sendActionOf(task)
                        return (
                          <React.Fragment key={task.taskId}>
                          <tr style={{ borderBottom: pendingId != null ? 'none' : '1px solid var(--shell-border)', background: checked ? 'var(--shell-bg)' : 'transparent' }}>
                            <td style={{ ...tdS, width: 32, textAlign: 'center' }}>
                              <Checkbox
                                checked={checked}
                                disabled={!canCheck || pendingId != null}
                                onChange={() => toggleOne(task.taskId)}
                              />
                            </td>
                            <td style={{ ...tdS, width: 20 }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: task.color }} />
                            </td>
                            <td style={{ ...tdS, fontWeight: 600, whiteSpace: 'nowrap' }}>{task.workTypeName}</td>
                            <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                              {periods[task.taskId] ?? `${fmtDate(task.startDate)} ~ ${fmtDate(task.endDate)}`}
                            </td>
                            <td style={{ ...tdS, padding: '5px 10px' }}>
                              {/* 후보 펼침 목록 — 행을 펼칠 때 이 공종 후보만 조회(loadOptions)하고 캐시한다.
                                  서버 정렬 순서 그대로 PartnerCandidateItem 으로 표시(재정렬·순위 배지 없음). */}
                              <Combobox
                                value={comboValue}
                                displayValue={unmatchedAssigned ? `${task.foremanName}(삭제됨)` : (tmpPartner?.partnerName ?? foremanNames[task.taskId] ?? task.foremanName ?? '시공파트너 선택')}
                                disabled={assigningTaskId === task.taskId || changingTaskId === task.taskId}
                                loadOptions={() => loadCandidateOptions(task)}
                                renderOption={opt => renderCandidateOption(task, opt)}
                                placeholder="시공파트너 선택"
                                onChange={val => {
                                  if (val === comboValue) return
                                  handlePickCandidate(task, val)
                                }}
                                size="sm"
                                className="!min-w-[176px] !max-w-[176px] [&_.menu]:!w-[440px]"
                              />
                              {displayPhone && (
                                <div style={{ marginTop: 'var(--spacing-01)', paddingLeft: 'var(--spacing-01)', fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', whiteSpace: 'nowrap' }}>{displayPhone}</div>
                              )}
                            </td>
                            <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{assignBadge(task, typeof tmpPartnerId === 'number' || !!task.foremanName)}</td>
                            <td style={{ ...tdS, fontSize: 'var(--type-caption-size)', wordBreak: 'keep-all', color: rowAction.enabled ? 'var(--shell-on-surface)' : 'var(--shell-hint)' }}>
                              {rowAction.label}
                            </td>
                          </tr>
                          {pendingId != null && (
                            <tr style={{ borderBottom: '1px solid var(--shell-border)' }}>
                              <td />
                              <td colSpan={6} style={{ padding: '0 10px 10px' }}>
                                <ForemanChangeNotice
                                  previousName={task.foremanName}
                                  reason={changeReasons[task.taskId] ?? ''}
                                  onReasonChange={r => {
                                    setChangeReasons(prev => ({ ...prev, [task.taskId]: r }))
                                    setChangeErrors(prev => { const n = { ...prev }; delete n[task.taskId]; return n })
                                  }}
                                  disabled={changingTaskId === task.taskId}
                                />
                                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <Button
                                    variant="secondary" size="sm"
                                    loading={changingTaskId === task.taskId}
                                    disabled={changingTaskId != null}
                                    onClick={() => { void handleConfirmChange(task, true) }}
                                  >일정확인 카톡 보내기</Button>
                                  <Button
                                    variant="etc" size="sm"
                                    disabled={changingTaskId != null}
                                    onClick={() => { void handleConfirmChange(task, false) }}
                                  >변경 저장</Button>
                                  <Button
                                    variant="etc" size="sm"
                                    disabled={changingTaskId != null}
                                    onClick={() => clearPendingChange(task.taskId)}
                                  >취소</Button>
                                </div>
                                {changeErrors[task.taskId] && (
                                  <div className="field error" style={{ marginTop: 6, maxWidth: 'none' }} role="alert"><span className="help">{changeErrors[task.taskId]}</span></div>
                                )}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* 발송 버튼 */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--shell-border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0, background: 'var(--shell-bg)' }}>
                  <button
                    onClick={handleSend}
                    disabled={sendTargets.length === 0 || sending}
                    style={{ padding: '10px 28px', borderRadius: 12, fontSize: 13, fontWeight: 700, border: 'none', background: sendTargets.length > 0 && !sending ? 'var(--brand-kakao-container)' : 'var(--shell-skeleton)', color: sendTargets.length > 0 && !sending ? 'var(--brand-kakao-label)' : 'var(--shell-on-surface-2)', cursor: sendTargets.length > 0 && !sending ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}
                  >
                    {sending ? '발송 중...' : sendTargets.length === 0 ? '알림톡을 발송할 시공파트너를 체크해 주세요' : `체크한 시공파트너 ${sendPartnerCount}명에게 일정확인 알림톡 발송`}
                  </button>
                </div>
              </div>

              {/* 우측: 카카오 알림톡 예시 */}
              <div style={{ width: 360, borderLeft: '1px solid var(--shell-border)', display: 'flex', flexDirection: 'column', background: 'var(--shell-skeleton)', flexShrink: 0 }}>
                <div style={{ padding: '12px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>카카오 알림톡 예시</span>
                  {previewList.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button
                        onClick={() => setPreviewIdx(i => Math.max(0, i - 1))}
                        disabled={safeIdx === 0}
                        aria-label="이전 공종"
                        style={{ width: 20, height: 20, borderRadius: '50%', border: '1px solid var(--shell-border)', background: safeIdx === 0 ? 'var(--shell-skeleton)' : 'var(--shell-surface)', color: safeIdx === 0 ? 'var(--shell-hint)' : 'var(--shell-on-surface)', cursor: safeIdx === 0 ? 'default' : 'pointer', fontSize: 'var(--type-caption-size)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', lineHeight: 1, flexShrink: 0 }}>‹</button>
                      <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface)', fontWeight: 700, minWidth: 32, textAlign: 'center' }}>{safeIdx + 1} / {previewList.length}</span>
                      <button
                        onClick={() => setPreviewIdx(i => Math.min(previewList.length - 1, i + 1))}
                        disabled={safeIdx === previewList.length - 1}
                        aria-label="다음 공종"
                        style={{ width: 20, height: 20, borderRadius: '50%', border: '1px solid var(--shell-border)', background: safeIdx === previewList.length - 1 ? 'var(--shell-skeleton)' : 'var(--shell-surface)', color: safeIdx === previewList.length - 1 ? 'var(--shell-hint)' : 'var(--shell-on-surface)', cursor: safeIdx === previewList.length - 1 ? 'default' : 'pointer', fontSize: 'var(--type-caption-size)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', lineHeight: 1, flexShrink: 0 }}>›</button>
                    </div>
                  )}
                </div>
                <div style={{ flex: 1, padding: '0 14px 14px', overflowY: 'auto' }}>
                  <Segmented
                    size="sm"
                    className="!grid grid-cols-4 !w-full whitespace-nowrap"
                    style={{ marginBottom: 10 }}
                    options={NOTICE_TAB_OPTIONS}
                    value={noticeTab}
                    onChange={v => setNoticeTab(v as NoticeTab)}
                  />
                  <div style={{ borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--elevation-2)' }}>
                    <div style={{ background: 'var(--brand-kakao-symbol)', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontSize: 20, lineHeight: 1 }}>💬</div>
                      <div>
                        <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--brand-kakao-container)', letterSpacing: '.4px' }}>카카오 알림톡</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-surface)' }}>인디플래너</div>
                      </div>
                    </div>
                    <div style={{ background: 'var(--brand-kakao-container)', padding: '6px 14px', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--brand-kakao-label)' }}>
                      {siteName || '인디빌라 3층 인테리어'}
                    </div>
                    {(() => {
                      const items = noticeTaskId != null ? noticeCache[noticeTaskId] : undefined
                      if (noticeTaskId == null || (!items && !noticeFailed.has(noticeTaskId))) {
                        return (
                          <div style={{ background: 'var(--shell-surface)', padding: '12px 14px' }} aria-busy={noticeTaskId != null}>
                            {noticeTaskId == null
                              ? <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>예시를 볼 공종이 없어요</span>
                              : <><Skeleton height={14} /><div style={{ height: 6 }} /><Skeleton height={14} /><div style={{ height: 6 }} /><Skeleton height={80} /></>}
                          </div>
                        )
                      }
                      if (!items) {
                        return (
                          <div style={{ background: 'var(--shell-surface)', padding: '12px 14px', fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>
                            예시를 불러오지 못했어요
                          </div>
                        )
                      }
                      const byKind = (k: NoticePreviewItem['kind']) => items.find(i => i.kind === k)
                      const main = byKind(noticeTab === 'CLOSED' ? 'CLOSED_EXPIRED' : noticeTab)
                      const replaced = noticeTab === 'CLOSED' ? byKind('CLOSED_REPLACED') : undefined
                      const bodyStyle: React.CSSProperties = { fontSize: 'var(--type-caption-size)', lineHeight: 1.7, color: 'var(--shell-on-surface)', whiteSpace: 'pre-line' }
                      return (
                        <>
                          <div style={{ background: 'var(--shell-surface)', padding: '12px 14px' }}>
                            <div style={bodyStyle}>{main?.content ?? ''}</div>
                            {replaced && (
                              <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed var(--shell-border)' }}>
                                <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', marginBottom: 4 }}>기한 전에 다른 시공파트너로 바꾸는 경우</div>
                                <div style={{ ...bodyStyle, color: 'var(--shell-on-surface-2)' }}>{replaced.content}</div>
                              </div>
                            )}
                          </div>
                          {main && main.buttons.length > 0 && (
                            <div style={{ display: 'flex', borderTop: '1px solid var(--shell-border)' }}>
                              {main.buttons.map((label, idx) => (
                                <button
                                  key={label}
                                  style={{ flex: 1, padding: '10px 0', background: idx === 0 ? 'var(--brand-kakao-container)' : 'var(--shell-surface)', fontSize: 'var(--type-caption-size)', fontWeight: 700, border: 'none', borderRight: idx < main.buttons.length - 1 ? '1px solid var(--shell-border)' : undefined, cursor: 'default', fontFamily: 'inherit', color: idx === 0 ? 'var(--brand-kakao-label)' : 'var(--shell-on-surface)' }}
                                >{label}</button>
                              ))}
                            </div>
                          )}
                        </>
                      )
                    })()}
                  </div>
                  <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', textAlign: 'center', marginTop: 10 }}>실제 발송 내용과 유사합니다</div>
                </div>
              </div>

            </div>
          )}

          {/* ── 회신 결과 탭 ── (서버 foreman-confirm-results 응답만 표시) */}
          {tab === 'result' && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {scheduleId == null ? (
                <EmptyState title="현장 정보가 없어 회신 결과를 볼 수 없어요" />
              ) : resultsState === 'loading' && !results ? (
                <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }} aria-busy="true">
                  <Skeleton height={28} />
                  <Skeleton height={56} />
                  <Skeleton height={56} />
                  <Skeleton height={120} />
                </div>
              ) : resultsState === 'error' || !results ? (
                <div style={{ padding: 20 }}>
                  <ErrorBanner message="회신 결과를 불러오지 못했어요" onRetry={() => { void loadResults() }} />
                </div>
              ) : (() => {
                const urgentItems = results.actionRequired
                const autoItems   = results.autoScheduled
                const autoSendItems = autoItems.filter(i => i.plan === 'AUTO_SEND')
                const earliestAt  = autoSendItems.map(i => i.scheduledAt).filter((v): v is string => !!v).sort()[0]
                const taskColor   = (taskId: number) => tasks.find(t => t.taskId === taskId)?.color ?? 'var(--shell-hint)'
                const historyCountByTask = new Map<number, number>()
                results.histories.forEach(h => historyCountByTask.set(h.taskId, (historyCountByTask.get(h.taskId) ?? 0) + 1))
                // 마감 안내 시각 — 같은 공종 이력을 attemptNo(없으면 발송시각) 오름차순으로 볼 때 바로 다음 행의 발송시각
                const closedNoticeAt = new Map<number, string>()
                const byTask = new Map<number, typeof results.histories>()
                results.histories.forEach(h => { const l = byTask.get(h.taskId) ?? []; l.push(h); byTask.set(h.taskId, l) })
                byTask.forEach(list => {
                  const sorted = [...list].sort((x, y) =>
                    (x.attemptNo != null && y.attemptNo != null ? x.attemptNo - y.attemptNo : 0)
                    || (x.sentDtm ?? '').localeCompare(y.sentDtm ?? '')
                    || x.historyId - y.historyId)
                  sorted.forEach((h, idx) => { const nx = sorted[idx + 1]; if (nx?.sentDtm) closedNoticeAt.set(h.historyId, nx.sentDtm) })
                })
                const periodOf = (s: string, e: string) => `${formatMonthDay(s)} ~ ${formatMonthDay(e)}`

                // 현재 줄 — 반장 · 불가 회신 시각 · 기한
                const currentLine = (item: ForemanConfirmTaskItem) => {
                  const parts: string[] = []
                  if (item.partnerName) parts.push(item.partnerName)
                  if (item.partnerStatus === 'DECLINED' && item.respondedDtm) parts.push(`‘불가’ 회신 ${formatMonthDayTime(item.respondedDtm)}`)
                  if (item.replyExpireDtm) parts.push(`기한 ${formatMonthDayTime(item.replyExpireDtm)}`)
                  return parts.join(' · ')
                }

                const renderRow = (item: ForemanConfirmTaskItem, urgent: boolean) => {
                  const badge = urgent
                    ? (item.reason ? ACTION_REASON_LABEL[item.reason] : undefined)
                    : (item.plan ? PLAN_LABEL[item.plan] : undefined)
                  const target = mainTargetOf(item, urgent)
                  const busy = busyTaskIds.has(item.taskId)
                  const cands = candResultRef.current.get(item.taskId)
                  const otherOpen = openMenu?.taskId === item.taskId && openMenu.kind === 'other'
                  const moreOpen  = openMenu?.taskId === item.taskId && openMenu.kind === 'more'
                  const line = currentLine(item)
                  // 부가 문구
                  let extra: React.ReactNode = null
                  if (urgent && !target) extra = '보낼 반장님이 없어요'
                  else if (!urgent && item.plan === 'AUTO_SEND' && item.autoNextPartner) extra = <>기한이 지나면 자동으로 <b>{item.autoNextPartner.partnerName}</b> 님께</>
                  else if (!urgent && item.plan === 'UNFIXED_ON_EXPIRE' && item.reasonCode) extra = PLAN_REASON_TEXT[item.reasonCode] ?? null
                  const fail = item.failReason ? (FAIL_REASON_LABEL[item.failReason] ?? '발송 실패') : null

                  return (
                    <div key={item.taskId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--shell-border)' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: taskColor(item.taskId), flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{item.workTypeName}</span>
                          {badge && <Badge status={badge.status}>{badge.label}</Badge>}
                          {fail && <Badge status="danger">{fail}</Badge>}
                        </div>
                        {line && <div style={{ marginTop: 2, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>{line}</div>}
                        {extra && <div style={{ marginTop: 2, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface)' }}>{extra}</div>}
                      </div>

                      {/* 액션 3칸: ① 주버튼 ② 다른 반장님 ▾ ③ ⋯ */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <div style={{ minWidth: 150, display: 'flex', justifyContent: 'flex-end' }}>
                          {target ? (
                            <Button
                              variant={urgent ? 'primary' : 'secondary'}
                              size="sm"
                              loading={busy}
                              disabled={busy || bulkSending}
                              onClick={() => { void handleMainSend(item, urgent) }}
                            >
                              {target.partnerName} 님께 {urgent ? '보내기' : '지금 보내기'}
                            </Button>
                          ) : urgent ? (
                            <Button variant="primary" size="sm" onClick={askRegisterPartner}>반장님 등록</Button>
                          ) : null}
                        </div>

                        <div className="menu-anchor" ref={otherOpen ? menuRootRef : undefined}>
                          <Button
                            variant="etc"
                            size="sm"
                            aria-haspopup="menu"
                            aria-expanded={otherOpen}
                            disabled={busy || bulkSending}
                            onClick={() => openOtherMenu(item.taskId)}
                          >
                            다른 반장님 ▾
                          </Button>
                          {otherOpen && (
                            <div className="menu show" role="menu" style={{ left: 'auto', right: 0, width: 440, maxHeight: 280, overflowY: 'auto' }}>
                              {candErrorIds.has(item.taskId) ? (
                                <div style={{ padding: 10, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ color: 'var(--status-danger-600)' }}>후보를 불러오지 못했어요</span>
                                  <Button variant="etc" size="sm" onClick={() => openOtherMenu(item.taskId, true)}>다시 시도</Button>
                                </div>
                              ) : !cands ? (
                                <div style={{ padding: 10, fontSize: 12, color: 'var(--shell-on-surface-2)' }}>불러오는 중…</div>
                              ) : cands.candidates.length === 0 ? (
                                <div style={{ padding: 10, fontSize: 12, lineHeight: 1.5 }}>
                                  <div style={{ fontWeight: 600 }}>배정 가능한 시공파트너가 없어요</div>
                                  <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>
                                    나의 시공파트너에 &lsquo;{item.workTypeName}&rsquo; 작업 파트너를 먼저 등록해 주세요
                                  </div>
                                </div>
                              ) : cands.candidates.map(c => (
                                <div
                                  key={c.partnerId}
                                  role="menuitem"
                                  aria-disabled={c.alreadySent}
                                  className={`menu-item${otherTargets[item.taskId]?.partnerId === c.partnerId ? ' selected' : ''}`}
                                  style={c.alreadySent ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                  onClick={() => handlePickOther(item, c)}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}><PartnerCandidateItem candidate={c} compact /></div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div style={{ width: 32, display: 'flex', justifyContent: 'center' }}>
                          {item.resendAvailable && (
                            <div className="menu-anchor" ref={moreOpen ? menuRootRef : undefined}>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label="더보기"
                                aria-haspopup="menu"
                                aria-expanded={moreOpen}
                                disabled={busy || bulkSending}
                                onClick={() => setOpenMenu(moreOpen ? null : { taskId: item.taskId, kind: 'more' })}
                              >
                                ⋯
                              </Button>
                              {moreOpen && (
                                <div className="menu show" role="menu" style={{ left: 'auto', right: 0, minWidth: 220 }}>
                                  <div role="menuitem" className="menu-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }} onClick={() => { void handleResendRow(item) }}>
                                    <span style={{ fontWeight: 600 }}>{item.partnerName ?? '현재 반장'} 님께 다시 보내기</span>
                                    <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>반장별 1번 · 기한 새로 계산</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                }

                return (
                  <>
                    {/* 상단 요약 */}
                    <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--shell-border)', background: 'var(--shell-surface)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)', marginRight: 6 }}>일정확인 회신 결과</span>
                      <Badge className="badge-done">발송 {results.summary.sentPartnerCount}명</Badge>
                      <Badge status="success">✓ 가능 {results.summary.confirmedCount}</Badge>
                      <Badge status="warning">⏳ 회신 대기 {results.summary.waitingCount}</Badge>
                      {recommendedItems.length > 0 && (
                        <Button variant="primary" size="sm" className="ml-auto" loading={bulkSending} disabled={bulkSending} onClick={askSendRecommended}>
                          추천대로 모두 보내기 ({recommendedItems.length}건)
                        </Button>
                      )}
                    </div>

                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                      {/* 답장 기한 안내 — 사업장 설정값 */}
                      {(() => {
                        const unit = results.settings.durationUnit === 'HOUR' ? '시간' : '일'
                        const dur = `${results.settings.replyDuration}${unit}`
                        return (
                          <div style={{ padding: '10px 20px 0', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface)' }}>
                              <b>답장 기한 {dur}</b> · 보낸 뒤 {dur} 이내에 답장을 받아요. 착공 하루 전 07:00(아침 7시)이 더 빠르면 그때까지입니다.
                            </span>
                            {onOpenSettings && (
                              <TextLink onClick={() => { onClose(); onOpenSettings() }}>설정에서 바꾸기 ›</TextLink>
                            )}
                          </div>
                        )
                      })()}
                      {/* 자동 발송 안내 */}
                      <div style={{ padding: '10px 20px 0' }}>
                        <Alert kind="info">
                          <span>
                            {!results.settings.autoNext
                              ? '자동 발송이 꺼져 있어요 — 직접 보내 주세요'
                              : autoSendItems.length === 0
                                ? '자동으로 보낼 예정인 공정이 없어요'
                                : `손대지 않으면 ${formatMonthDayTime(earliestAt)}에 자동으로 보내요 (${autoSendItems.map(i => `${i.workTypeName} → ${i.autoNextPartner?.partnerName ?? '다음 반장'}`).join(' · ')})`}
                          </span>
                        </Alert>
                      </div>

                      {/* 🔴 지금 처리할 일 */}
                      <div style={{ padding: '14px 20px 6px', fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' }}>
                        {urgentItems.length > 0 ? `🔴 지금 처리할 일 ${urgentItems.length}` : <span style={{ color: 'var(--shell-on-surface-2)', fontWeight: 600 }}>🔴 지금 처리할 일이 없어요</span>}
                      </div>
                      {urgentItems.map(i => renderRow(i, true))}

                      {/* 🟡 자동 처리 예정 — 0건이면 그룹 숨김 */}
                      {autoItems.length > 0 && (
                        <>
                          <div style={{ padding: '14px 20px 6px', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' }}>🟡 자동 처리 예정 {autoItems.length}</span>
                            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>그냥 두어도 기한에 자동으로 보내요 · 앞당기려면 [지금 보내기]</span>
                          </div>
                          {autoItems.map(i => renderRow(i, false))}
                        </>
                      )}

                      {/* 전체 발송 이력 */}
                      <div style={{ padding: '18px 20px 6px', fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' }}>전체 발송 이력</div>
                      {results.histories.length === 0 ? (
                        <EmptyState title="발송 이력이 없어요" />
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={thS}>공종</th>
                              <th style={thS}>작업기간</th>
                              <th style={thS}>시공파트너</th>
                              <th style={thS}>연락처</th>
                              <th style={thS}>발송일시</th>
                              <th style={thS}>회신결과</th>
                              <th style={thS}>회신일시</th>
                              <th style={thS}>기한 · 마감 안내</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.histories.map(h => {
                              const closed = h.status === 'CLOSED'
                              const badge = HISTORY_STATUS_BADGE[h.status]
                              const multi = (historyCountByTask.get(h.taskId) ?? 0) >= 2
                              const attemptText = multi && h.attemptNo != null ? `${h.attemptNo}번째` : ''
                              const methodText = h.sendMethod ? (SEND_METHOD_LABEL[h.sendMethod] ?? '') : ''
                              const dim: React.CSSProperties = closed ? { color: 'var(--shell-hint)' } : {}
                              let due: React.ReactNode = '—'
                              if (closed) {
                                const reason = (h.closedReason ? CLOSED_REASON_LABEL[h.closedReason] : null) ?? '마감'
                                const noticeAt = closedNoticeAt.get(h.historyId)
                                due = (
                                  <>
                                    <div>{reason}</div>
                                    {noticeAt && <div style={{ fontSize: 'var(--type-caption-size)' }}>마감 안내 {formatMonthDayTime(noticeAt)}</div>}
                                  </>
                                )
                              }
                              else if (h.status === 'WAITING') due = formatMonthDayTime(h.replyExpireDtm) || '—'
                              else if (h.status === 'EXPIRED') due = <span style={{ color: 'var(--status-danger-600)', fontWeight: 700 }}>{formatMonthDayTime(h.replyExpireDtm) || '기한 지남'}</span>
                              return (
                                <tr key={h.historyId} style={{ borderBottom: '1px solid var(--shell-border)', opacity: closed ? 0.7 : 1, background: closed ? 'var(--shell-skeleton)' : undefined }}>
                                  <td style={{ ...tdS, ...dim }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: taskColor(h.taskId), flexShrink: 0, opacity: closed ? 0.4 : 1 }} />
                                      <span style={{ fontWeight: 600 }}>{h.workTypeName}</span>
                                    </div>
                                  </td>
                                  <td style={{ ...tdS, ...dim, whiteSpace: 'nowrap' }}>{periodOf(h.taskStartDate, h.taskEndDate)}</td>
                                  <td style={{ ...tdS, ...dim }}>
                                    <div>{h.partnerName ?? '—'}</div>
                                    {attemptText && <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>{attemptText}</div>}
                                  </td>
                                  <td style={{ ...tdS, ...dim, whiteSpace: 'nowrap' }}>{h.partnerPhone ?? '–'}</td>
                                  <td style={{ ...tdS, ...dim, whiteSpace: 'nowrap' }}>
                                    <div>{formatMonthDayTime(h.sentDtm) || '—'}</div>
                                    {methodText && <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>{methodText}</div>}
                                  </td>
                                  <td style={tdS}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                      {badge && <Badge status={badge.status} className="whitespace-nowrap">{badge.label}</Badge>}
                                      {h.deliveryFailed && <Badge status="danger" className="whitespace-nowrap">전달 안 됨</Badge>}
                                    </div>
                                  </td>
                                  <td style={{ ...tdS, ...dim, whiteSpace: 'nowrap' }}>{formatMonthDayTime(h.respondedDtm) || '—'}</td>
                                  <td style={{ ...tdS, ...dim, whiteSpace: 'nowrap' }}>{due}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>

                    <div style={{ padding: '10px 20px', borderTop: '1px solid var(--shell-border)', fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', background: 'var(--shell-bg)', flexShrink: 0 }}>
                      다음 반장님께 보내면 이전 반장님 요청은 마감되고 마감 안내 카톡이 가요
                    </div>
                  </>
                )
              })()}
            </div>
          )}

        </div>
      </div>

      {showPartnerPopup && (
        <PartnerFormPopup
          onClose={() => setShowPartnerPopup(false)}
          onSuccess={() => {
            setShowPartnerPopup(false)
            onPartnerCreated?.()
            // 새 파트너가 후보·추천에 들어와야 하므로 후보 캐시를 비우고 회신 결과를 다시 받는다.
            invalidateCandidates()
            void loadResults(true)
          }}
        />
      )}

      {immediate.modal}

      {evidenceTask && (
        <AssignEvidenceModal open onClose={() => setEvidenceTask(null)} taskId={evidenceTask.taskId} partnerName={evidenceTask.foremanName} />
      )}

      {/* 확인 모달 — 일정 겹침 확인 · 추천대로 모두 보내기 · 반장님 등록 안내 공용 */}
      <Modal
        open={confirmState != null}
        onClose={() => setConfirmState(null)}
        title={confirmState?.title}
        footerAlign="stretch"
        footer={confirmState && (
          <>
            <Button variant="secondary" onClick={() => setConfirmState(null)}>취소</Button>
            <Button
              variant="primary"
              onClick={() => { const c = confirmState; setConfirmState(null); c.onConfirm() }}
            >
              {confirmState.confirmLabel}
            </Button>
          </>
        )}
      >
        {confirmState && (
          <>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--shell-on-surface)' }}>{confirmState.body}</div>
          </>
        )}
      </Modal>
    </div>
  )
}
